import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label } from '@/components/ui'
import { Terminal, CheckCircle, XCircle, Mail, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

type VerifyState = 'loading' | 'success' | 'error' | 'no-token'

export function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refreshUser, resendVerification } = useAuth()
  const token = searchParams.get('token')

  const [state, setState] = useState<VerifyState>(token ? 'loading' : 'no-token')
  const [errorMsg, setErrorMsg] = useState('')
  const [expiredEmail, setExpiredEmail] = useState<string | null>(null)
  const [resendEmail, setResendEmail] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resending, setResending] = useState(false)

  useEffect(() => {
    if (!token) return

    const verify = async () => {
      try {
        const res = await fetch('/api/auth/verify-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        })
        const data = await res.json()
        if (res.ok) {
          setState('success')
          await refreshUser()
          setTimeout(() => navigate('/'), 2000)
        } else {
          setState('error')
          setErrorMsg(data.error || 'Verification failed')
          if (data.expired && data.email) {
            setExpiredEmail(data.email)
          }
        }
      } catch {
        setState('error')
        setErrorMsg('Network error. Please try again.')
      }
    }

    verify()
  }, [token])

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const handleResend = async (email: string) => {
    if (!email || resendCooldown > 0) return
    setResending(true)
    const result = await resendVerification(email)
    setResending(false)
    if (result.success) {
      toast.success('Verification email sent!')
      setResendCooldown(60)
    } else {
      toast.error(result.error || 'Failed to resend')
      if (result.retryAfter) setResendCooldown(result.retryAfter)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-3">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-primary/10 border border-primary/30 shadow-glow">
            <Terminal className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-primary glow-text">SCB|PH Terminal</h1>
        </div>

        <Card className="glass">
          {state === 'loading' && (
            <>
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-12 w-12 text-primary animate-spin" />
                </div>
                <CardTitle>Verifying Email</CardTitle>
                <CardDescription>Please wait while we verify your email address...</CardDescription>
              </CardHeader>
            </>
          )}

          {state === 'success' && (
            <>
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-12 w-12 text-green-500" />
                </div>
                <CardTitle>Email Verified!</CardTitle>
                <CardDescription>Your email has been verified successfully. Redirecting you to the terminal...</CardDescription>
              </CardHeader>
            </>
          )}

          {state === 'error' && (
            <>
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <XCircle className="h-12 w-12 text-destructive" />
                </div>
                <CardTitle>Verification Failed</CardTitle>
                <CardDescription>{errorMsg}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {expiredEmail && (
                  <Button
                    className="w-full"
                    onClick={() => handleResend(expiredEmail)}
                    disabled={resending || resendCooldown > 0}
                  >
                    {resending ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </span>
                    ) : resendCooldown > 0 ? (
                      `Resend in ${resendCooldown}s`
                    ) : (
                      <span className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Resend Verification Email
                      </span>
                    )}
                  </Button>
                )}
                <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>
                  Back to Login
                </Button>
              </CardContent>
            </>
          )}

          {state === 'no-token' && (
            <>
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Mail className="h-12 w-12 text-primary" />
                </div>
                <CardTitle>Check Your Email</CardTitle>
                <CardDescription>
                  We sent a verification link to your email address. Click the link to verify your account.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resend-email">Didn't receive it? Enter your email to resend:</Label>
                  <Input
                    id="resend-email"
                    type="email"
                    placeholder="Enter your email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={() => handleResend(resendEmail)}
                  disabled={resending || resendCooldown > 0 || !resendEmail}
                >
                  {resending ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </span>
                  ) : resendCooldown > 0 ? (
                    `Resend in ${resendCooldown}s`
                  ) : (
                    <span className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Resend Verification Email
                    </span>
                  )}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>
                  Back to Login
                </Button>
              </CardContent>
            </>
          )}
        </Card>

        <div className="text-center space-y-1">
          <div className="text-xs text-muted-foreground font-mono">
            <span className="text-primary">{'>'}</span> Secure connection established
          </div>
        </div>
      </div>
    </div>
  )
}
